/**
 * Revisiones, anomalías y actas.
 *
 * §§32-39 del prompt maestro. Es el módulo donde el trabajo de campo se
 * convierte en un documento con valor legal, y por eso es donde más caro
 * sale improvisar: un acta a la que le falta un dato del **anexo II §5.a**
 * no es un acta con una errata, es un acta **no conforme**. Si la CCAA la
 * pide (art. 21.2: a su disposición durante 5 años) el problema no lo tiene
 * el técnico, lo tiene la empresa.
 *
 * Por eso aquí no hay «campos recomendados». Hay una lista cerrada trazada
 * a su artículo, y un acta no se emite si falta alguno.
 *
 * Trazabilidad → `docs/01-normativa/MATRIZ-NORMATIVA.md`
 *   D-01  art. 17.c        informe técnico al titular (documento DISTINTO del acta)
 *   D-04  anexo II §5.a    contenido mínimo de la cabecera del acta
 *   D-05  anexo II §5.b    detalle obligatorio por producto/sistema
 *   D-06  anexo II §5      doble firma: mantenedora y propiedad
 *   D-10  art. 21.2        actas firmadas por personal cualificado, ≥5 años
 */

import { ConflictError, ValidationError } from './errors.js';

// ═══════════════════════════════════════════════════════════════════════
// 1. Estado de la revisión
// ═══════════════════════════════════════════════════════════════════════

export const INSPECTION_STATUSES = [
  /** Abierta en el dispositivo, se están registrando resultados. */
  'EN_CURSO',
  /** Todos los equipos previstos tienen resultado o justificación. */
  'COMPLETADA',
  /** Acta generada, pendiente de firmas. */
  'ACTA_GENERADA',
  /** Firmada por ambas partes: a partir de aquí no se toca. */
  'FIRMADA',
  /** Anulada antes de firmar, con motivo. */
  'ANULADA',
] as const;
export type InspectionStatus = (typeof INSPECTION_STATUSES)[number];

const INSPECTION_TRANSITIONS: Readonly<Record<InspectionStatus, readonly InspectionStatus[]>> = {
  EN_CURSO: ['COMPLETADA', 'ANULADA'],
  // Se puede volver a EN_CURSO: aparece un equipo que se creía inaccesible.
  COMPLETADA: ['EN_CURSO', 'ACTA_GENERADA', 'ANULADA'],
  // Regenerar el acta antes de firmarla es legítimo (se corrige un dato).
  ACTA_GENERADA: ['COMPLETADA', 'FIRMADA', 'ANULADA'],
  // 🔒 Estado terminal. Una revisión firmada se corrige emitiendo otra.
  FIRMADA: [],
  ANULADA: [],
};

export function canTransitionInspection(from: InspectionStatus, to: InspectionStatus): boolean {
  return INSPECTION_TRANSITIONS[from].includes(to);
}

export function assertInspectionTransition(from: InspectionStatus, to: InspectionStatus): void {
  if (canTransitionInspection(from, to)) return;
  if (from === 'FIRMADA') {
    throw new ConflictError(
      'Una revisión firmada no se modifica: la corrección se hace emitiendo una revisión nueva que sustituya a la anterior',
      { from, to },
    );
  }
  throw new ConflictError(`Transición de revisión no permitida: ${from} → ${to}`, { from, to });
}

// ═══════════════════════════════════════════════════════════════════════
// 2. Resultado por equipo
// ═══════════════════════════════════════════════════════════════════════

/**
 * Por qué un equipo previsto puede quedarse sin revisar.
 *
 * ⚠️ Esto **no** es burocracia. Un extintor tras una estantería sigue
 * teniendo su obligación de mantenimiento vencida: dejarlo fuera del acta
 * sin decirlo lo convertiría en un equipo «revisado» que nadie ha tocado.
 * El §37 exige que los equipos inaccesibles aparezcan en el informe.
 */
export const NOT_REVIEWED_REASONS = [
  'INACCESIBLE',
  'NO_LOCALIZADO',
  'ZONA_CERRADA',
  'EN_TALLER',
  'RETIRADO_POR_CLIENTE',
  'SIN_TIEMPO',
] as const;
export type NotReviewedReason = (typeof NOT_REVIEWED_REASONS)[number];

/**
 * Clasificación de resultado.
 *
 * Se define en la tabla `result_classification`, no aquí: el §33 dice
 * «clasificaciones técnicas configurables» y «nunca inventar categorías
 * legales». Este tipo es sólo la forma que tiene el dato al llegar.
 */
export interface ResultClassification {
  readonly code: string;
  readonly label: string;
  /** 0 = correcto. A mayor número, peor. */
  readonly severity: number;
  /** Impide cerrar la revisión mientras no se resuelva o se justifique. */
  readonly blocksClosure: boolean;
  /** Genera una anomalía automáticamente. */
  readonly createsDefect: boolean;
}

export interface InspectionEquipmentLine {
  readonly equipmentId: string;
  /** Marcado a `false` con motivo obligatorio. */
  readonly reviewed: boolean;
  readonly notReviewedReason?: NotReviewedReason | null;
  readonly notReviewedNotes?: string | null;
  readonly resultCode?: string | null;
  /** Ítems del checklist respondidos / totales exigidos. */
  readonly answeredItems?: number;
  readonly requiredItems?: number;
}

export interface EquipmentLineIssue {
  readonly equipmentId: string;
  readonly code:
    | 'RESULTADO_AUSENTE'
    | 'MOTIVO_AUSENTE'
    | 'CHECKLIST_INCOMPLETO'
    | 'RESULTADO_DESCONOCIDO';
  readonly message: string;
}

/**
 * Comprueba que la línea de un equipo está en condiciones de entrar en el
 * acta. No decide si el resultado es bueno: decide si está **completo**.
 */
export function validateEquipmentLine(
  line: InspectionEquipmentLine,
  classifications: readonly ResultClassification[],
): EquipmentLineIssue[] {
  const issues: EquipmentLineIssue[] = [];

  if (!line.reviewed) {
    if (!line.notReviewedReason) {
      issues.push({
        equipmentId: line.equipmentId,
        code: 'MOTIVO_AUSENTE',
        message:
          'Un equipo no revisado exige motivo: sin él quedaría como revisado y su obligación seguiría vencida sin que nadie lo sepa',
      });
    }
    return issues;
  }

  if (!line.resultCode) {
    issues.push({
      equipmentId: line.equipmentId,
      code: 'RESULTADO_AUSENTE',
      message: 'El anexo II §5.b exige el resultado de las operaciones realizadas',
    });
  } else if (!classifications.some((c) => c.code === line.resultCode)) {
    issues.push({
      equipmentId: line.equipmentId,
      code: 'RESULTADO_DESCONOCIDO',
      message: `La clasificación «${line.resultCode}» no está definida en el catálogo de la empresa`,
    });
  }

  const required = line.requiredItems ?? 0;
  if (required > 0 && (line.answeredItems ?? 0) < required) {
    issues.push({
      equipmentId: line.equipmentId,
      code: 'CHECKLIST_INCOMPLETO',
      message: `Faltan ${required - (line.answeredItems ?? 0)} ítems obligatorios del checklist`,
    });
  }

  return issues;
}

// ═══════════════════════════════════════════════════════════════════════
// 3. Progreso de la revisión
// ═══════════════════════════════════════════════════════════════════════

export interface InspectionProgress {
  readonly plannedEquipment: number;
  readonly reviewed: number;
  /** No revisados **con motivo**. Los que no lo tienen son `pending`. */
  readonly notReviewed: number;
  readonly pending: number;
  /** Equipos con resultado que genera anomalía. */
  readonly withDefects: number;
  /** Equipos cuyo resultado impide cerrar (p. ej. fuera de servicio). */
  readonly blocking: number;
  readonly percentComplete: number;
  readonly notReviewedByReason: Readonly<Record<string, number>>;
}

export function summarizeInspection(
  lines: readonly InspectionEquipmentLine[],
  classifications: readonly ResultClassification[],
  plannedEquipment = lines.length,
): InspectionProgress {
  const byCode = new Map(classifications.map((c) => [c.code, c]));
  const notReviewedByReason: Record<string, number> = {};

  let reviewed = 0;
  let notReviewed = 0;
  let withDefects = 0;
  let blocking = 0;

  for (const line of lines) {
    if (!line.reviewed) {
      // Sin motivo no está «no revisado»: está PENDIENTE. Contarlo como
      // atendido daría por cerrada una revisión a medias.
      if (!line.notReviewedReason) continue;
      notReviewed++;
      notReviewedByReason[line.notReviewedReason] =
        (notReviewedByReason[line.notReviewedReason] ?? 0) + 1;
      continue;
    }
    reviewed++;
    const classification = line.resultCode ? byCode.get(line.resultCode) : undefined;
    if (classification?.createsDefect) withDefects++;
    if (classification?.blocksClosure) blocking++;
  }

  const attended = reviewed + notReviewed;
  return {
    plannedEquipment,
    reviewed,
    notReviewed,
    pending: Math.max(0, plannedEquipment - attended),
    withDefects,
    blocking,
    percentComplete: plannedEquipment === 0 ? 100 : Math.round((attended / plannedEquipment) * 100),
    notReviewedByReason,
  };
}

// ═══════════════════════════════════════════════════════════════════════
// 4. Anomalías (§34)
// ═══════════════════════════════════════════════════════════════════════

export const ANOMALY_STATUSES = [
  'DETECTADA',
  'NOTIFICADA',
  'PENDIENTE_PRESUPUESTO',
  'PRESUPUESTO_ENVIADO',
  'ACEPTADA',
  'PENDIENTE_INTERVENCION',
  'CORREGIDA',
  /** Comprobada por un técnico distinto o en una visita posterior. */
  'VERIFICADA',
  'CERRADA',
  /** El cliente decide no corregirla. Se cierra, pero deja constancia. */
  'RECHAZADA_CLIENTE',
] as const;
export type AnomalyStatus = (typeof ANOMALY_STATUSES)[number];

const ANOMALY_TRANSITIONS: Readonly<Record<AnomalyStatus, readonly AnomalyStatus[]>> = {
  // Una anomalía trivial puede ir directa a intervención sin presupuesto.
  DETECTADA: ['NOTIFICADA', 'PENDIENTE_PRESUPUESTO', 'PENDIENTE_INTERVENCION', 'RECHAZADA_CLIENTE'],
  NOTIFICADA: ['PENDIENTE_PRESUPUESTO', 'PENDIENTE_INTERVENCION', 'RECHAZADA_CLIENTE'],
  PENDIENTE_PRESUPUESTO: ['PRESUPUESTO_ENVIADO', 'RECHAZADA_CLIENTE'],
  PRESUPUESTO_ENVIADO: ['ACEPTADA', 'RECHAZADA_CLIENTE', 'PENDIENTE_PRESUPUESTO'],
  ACEPTADA: ['PENDIENTE_INTERVENCION'],
  PENDIENTE_INTERVENCION: ['CORREGIDA', 'PENDIENTE_PRESUPUESTO'],
  CORREGIDA: ['VERIFICADA', 'PENDIENTE_INTERVENCION'],
  VERIFICADA: ['CERRADA'],
  CERRADA: [],
  RECHAZADA_CLIENTE: ['PENDIENTE_PRESUPUESTO'],
};

export function canTransitionAnomaly(from: AnomalyStatus, to: AnomalyStatus): boolean {
  return ANOMALY_TRANSITIONS[from].includes(to);
}

export function assertAnomalyTransition(from: AnomalyStatus, to: AnomalyStatus): void {
  if (canTransitionAnomaly(from, to)) return;
  throw new ConflictError(`Transición de anomalía no permitida: ${from} → ${to}`, { from, to });
}

export const ANOMALY_SEVERITIES = ['LEVE', 'GRAVE', 'MUY_GRAVE'] as const;
export type AnomalySeverity = (typeof ANOMALY_SEVERITIES)[number];

export interface Anomaly {
  readonly id: string;
  readonly equipmentId: string | null;
  readonly inspectionId: string | null;
  readonly severity: AnomalySeverity;
  readonly status: AnomalyStatus;
  readonly detectedAt: string;
  /** Plazo de subsanación cuando lo fija un organismo de control (art. 22.4). */
  readonly dueDate?: string | null;
  /** Impide declarar el equipo en servicio. */
  readonly outOfService: boolean;
}

export interface AnomalyAgeing {
  readonly open: number;
  readonly overdue: number;
  readonly bySeverity: Readonly<Record<AnomalySeverity, number>>;
  readonly oldestOpenDays: number | null;
}

const CLOSED_ANOMALY_STATUSES: readonly AnomalyStatus[] = ['CERRADA', 'RECHAZADA_CLIENTE'];

export function isAnomalyOpen(status: AnomalyStatus): boolean {
  return !CLOSED_ANOMALY_STATUSES.includes(status);
}

/**
 * Antigüedad de las anomalías abiertas.
 *
 * El indicador que importa no es «cuántas hay» sino «cuánto llevan
 * abiertas»: una anomalía grave de hace ocho meses es una responsabilidad
 * documentada por escrito y sin corregir.
 */
export function summarizeAnomalies(anomalies: readonly Anomaly[], today: string): AnomalyAgeing {
  const bySeverity: Record<AnomalySeverity, number> = { LEVE: 0, GRAVE: 0, MUY_GRAVE: 0 };
  let open = 0;
  let overdue = 0;
  let oldestOpenDays: number | null = null;

  for (const anomaly of anomalies) {
    if (!isAnomalyOpen(anomaly.status)) continue;
    open++;
    bySeverity[anomaly.severity]++;
    if (anomaly.dueDate && anomaly.dueDate < today) overdue++;

    const age = daysBetween(anomaly.detectedAt.slice(0, 10), today);
    if (oldestOpenDays === null || age > oldestOpenDays) oldestOpenDays = age;
  }

  return { open, overdue, bySeverity, oldestOpenDays };
}

function daysBetween(from: string, to: string): number {
  const ms = Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`);
  return Math.round(ms / 86_400_000);
}

// ═══════════════════════════════════════════════════════════════════════
// 5. Contenido obligatorio del acta — anexo II §5
// ═══════════════════════════════════════════════════════════════════════

/**
 * Cabecera del acta (§5.a, fila D-04 de la matriz).
 *
 * La lista es **literal del reglamento**, no una interpretación. Cada campo
 * que falte se devuelve con su referencia para que el aviso al usuario diga
 * qué artículo se está incumpliendo, no un «campo obligatorio» genérico.
 */
export interface ActaHeader {
  /** §5.a.1 — nombre y domicilio de la propiedad. */
  readonly ownerName: string | null;
  readonly ownerAddress: string | null;
  /** §5.a.2 — representante responsable de la instalación. */
  readonly installationRepresentative: string | null;
  /** §5.a.3 — representante responsable ante las operaciones. */
  readonly operationsRepresentative: string | null;
  /** §5.a.4 — domicilio de la instalación y fecha de instalación. */
  readonly siteAddress: string | null;
  readonly installationDate: string | null;
  /** §5.a.5 — empresa y fecha de la última inspección. */
  readonly lastInspectionCompany: string | null;
  readonly lastInspectionDate: string | null;
  /** §5.a.6 — empresa y fecha del último mantenimiento. */
  readonly lastMaintenanceCompany: string | null;
  readonly lastMaintenanceDate: string | null;
  /** §5.a.7 — mantenedora, con declaración de habilitación por sistema. */
  readonly maintainerName: string | null;
  readonly maintainerRegistrationNumber: string | null;
  readonly maintainerAddress: string | null;
  readonly declaredAuthorizations: readonly string[];
  /** §5.a.8 — responsables de ejecución y declaración de cualificación. */
  readonly performers: readonly { readonly name: string; readonly qualifiedFor: readonly string[] }[];
  /** §5.a.9 — tipos de productos y sistemas objeto de mantenimiento. */
  readonly systemsCovered: readonly string[];
}

/** Detalle por producto o sistema (§5.b, fila D-05). */
export interface ActaEquipmentDetail {
  readonly equipmentId: string;
  /** §5.b — tipo, marca y modelo. */
  readonly equipmentType: string | null;
  readonly brand: string | null;
  readonly model: string | null;
  /**
   * §5.b — identificación unívoca. El reglamento admite «nº de serie,
   * ubicación…»: vale el código interno o la ubicación, pero algo tiene
   * que identificar el equipo sin ambigüedad.
   */
  readonly uniqueIdentification: string | null;
  /**
   * La ficha del equipo.
   *
   * El §5.b exige identificarlo, pero un acta que sólo dice «extintor nº 4»
   * obliga a ir al inventario para saber de qué extintor habla. Cuando el
   * cliente discute una factura o un inspector revisa el expediente, lo que
   * hace falta está aquí: qué equipo era, de cuándo, y por qué retimbrados ha
   * pasado.
   */
  readonly serialNumber?: string | null;
  readonly manufacturingDate?: string | null;
  /** «Polvo ABC 6 kg», compuesto a partir del tipo y la capacidad. */
  readonly description?: string | null;
  /** Fechas de los retimbrados, en orden. Vacío si no lleva ninguno. */
  readonly restampings?: readonly string[];
  /** Cuántos le quedan antes de la baja por vida útil. */
  readonly restampingsLeft?: number | null;
  /** Peso antes y después, sólo cuando ha habido retimbrado. */
  readonly weightBefore?: string | null;
  readonly weightAfter?: string | null;
  /** Piezas sustituidas: maneta, anilla, manómetro, junta… */
  readonly replacements?: readonly string[];

  /** §5.b — operaciones realizadas y su resultado. */
  readonly operations: readonly string[];
  readonly result: string | null;
  /** §5.b — en caso de incidencias, acciones propuestas. */
  readonly hasIncident: boolean;
  readonly proposedActions: readonly string[];
}

export interface ActaContent {
  readonly header: ActaHeader;
  readonly details: readonly ActaEquipmentDetail[];
  /** §37 — los equipos que no se pudieron revisar constan en el informe. */
  readonly notReviewed: readonly {
    readonly equipmentId: string;
    /**
     * Identificación legible del equipo, capturada al revisar.
     *
     * El acta la imprime en lugar del identificador técnico: el anexo II
     * §5.b exige identificar el equipo, y un UUID no identifica nada para
     * quien lea el documento.
     */
    readonly identification?: string | null;
    readonly reason: NotReviewedReason;
    readonly notes?: string | null;
  }[];
  /** D-06 — firmas de mantenedora y propiedad. */
  readonly maintainerSignatureId: string | null;
  readonly ownerSignatureId: string | null;
  /** ADR-007 — la versión normativa con la que se ejecutó, congelada. */
  readonly regulationVersionId: string | null;
}

export interface ActaIssue {
  readonly field: string;
  readonly articleRef: string;
  readonly message: string;
  readonly equipmentId?: string;
}

export interface ActaValidation {
  readonly conforme: boolean;
  readonly issues: readonly ActaIssue[];
}

const HEADER_REQUIREMENTS: readonly {
  field: keyof ActaHeader;
  ref: string;
  label: string;
}[] = [
  { field: 'ownerName', ref: 'RD 513/2017 anexo II §5.a.1', label: 'nombre de la propiedad' },
  { field: 'ownerAddress', ref: 'RD 513/2017 anexo II §5.a.1', label: 'domicilio de la propiedad' },
  { field: 'installationRepresentative', ref: 'RD 513/2017 anexo II §5.a.2', label: 'representante responsable de la instalación' },
  { field: 'operationsRepresentative', ref: 'RD 513/2017 anexo II §5.a.3', label: 'representante responsable ante las operaciones' },
  { field: 'siteAddress', ref: 'RD 513/2017 anexo II §5.a.4', label: 'domicilio de la instalación' },
  { field: 'installationDate', ref: 'RD 513/2017 anexo II §5.a.4', label: 'fecha de instalación' },
  { field: 'lastMaintenanceCompany', ref: 'RD 513/2017 anexo II §5.a.6', label: 'empresa del último mantenimiento' },
  { field: 'lastMaintenanceDate', ref: 'RD 513/2017 anexo II §5.a.6', label: 'fecha del último mantenimiento' },
  { field: 'maintainerName', ref: 'RD 513/2017 anexo II §5.a.7', label: 'nombre de la empresa mantenedora' },
  { field: 'maintainerRegistrationNumber', ref: 'RD 513/2017 anexo II §5.a.7', label: 'número de identificación de la mantenedora' },
  { field: 'maintainerAddress', ref: 'RD 513/2017 anexo II §5.a.7', label: 'domicilio de la mantenedora' },
];

/**
 * Valida el acta contra el contenido mínimo del anexo II §5.
 *
 * Devuelve **todos** los incumplimientos de golpe, no el primero: quien
 * está rellenando el acta necesita ver la lista completa, no descubrir los
 * huecos de uno en uno.
 */
export function validateActa(content: ActaContent): ActaValidation {
  const issues: ActaIssue[] = [];
  const { header } = content;

  for (const requirement of HEADER_REQUIREMENTS) {
    if (isBlank(header[requirement.field])) {
      issues.push({
        field: requirement.field,
        articleRef: requirement.ref,
        message: `Falta ${requirement.label}`,
      });
    }
  }

  // §5.a.5 — la última inspección puede no existir (instalación nueva o
  // no sujeta), pero si consta la fecha tiene que constar la empresa.
  if (!isBlank(header.lastInspectionDate) && isBlank(header.lastInspectionCompany)) {
    issues.push({
      field: 'lastInspectionCompany',
      articleRef: 'RD 513/2017 anexo II §5.a.5',
      message: 'Consta fecha de la última inspección pero no la empresa que la realizó',
    });
  }

  // §5.a.7 — la declaración de habilitación es POR sistema mantenido.
  // Sin ella el acta no es conforme aunque la empresa esté habilitada:
  // lo que exige el reglamento es que el acta lo declare.
  for (const system of header.systemsCovered) {
    if (!header.declaredAuthorizations.includes(system)) {
      issues.push({
        field: 'declaredAuthorizations',
        articleRef: 'RD 513/2017 anexo II §5.a.7',
        message: `El acta no declara la habilitación de la empresa para «${system}»`,
      });
    }
  }

  // §5.a.8 — y la cualificación es por técnico que ejecuta.
  if (header.performers.length === 0) {
    issues.push({
      field: 'performers',
      articleRef: 'RD 513/2017 anexo II §5.a.8',
      message: 'El acta no identifica a los responsables de la ejecución',
    });
  }
  for (const performer of header.performers) {
    const missing = header.systemsCovered.filter((s) => !performer.qualifiedFor.includes(s));
    if (missing.length > 0) {
      issues.push({
        field: 'performers',
        articleRef: 'RD 513/2017 anexo II §5.a.8',
        message: `El acta no declara la cualificación de ${performer.name} para: ${missing.join(', ')}`,
      });
    }
  }

  if (header.systemsCovered.length === 0) {
    issues.push({
      field: 'systemsCovered',
      articleRef: 'RD 513/2017 anexo II §5.a.9',
      message: 'El acta no indica los sistemas objeto de mantenimiento',
    });
  }

  // ── §5.b: detalle por producto o sistema ─────────────────────────────
  if (content.details.length === 0 && content.notReviewed.length === 0) {
    issues.push({
      field: 'details',
      articleRef: 'RD 513/2017 anexo II §5.b',
      message: 'El acta no contiene ningún producto ni sistema',
    });
  }

  for (const detail of content.details) {
    const missing: string[] = [];
    if (isBlank(detail.equipmentType)) missing.push('tipo');
    if (isBlank(detail.brand)) missing.push('marca');
    if (isBlank(detail.model)) missing.push('modelo');
    if (missing.length > 0) {
      issues.push({
        field: 'details',
        equipmentId: detail.equipmentId,
        articleRef: 'RD 513/2017 anexo II §5.b',
        message: `Falta ${missing.join(', ')} del equipo`,
      });
    }
    if (isBlank(detail.uniqueIdentification)) {
      issues.push({
        field: 'uniqueIdentification',
        equipmentId: detail.equipmentId,
        articleRef: 'RD 513/2017 anexo II §5.b',
        message: 'Falta la identificación unívoca del equipo (nº de serie, ubicación…)',
      });
    }
    if (detail.operations.length === 0) {
      issues.push({
        field: 'operations',
        equipmentId: detail.equipmentId,
        articleRef: 'RD 513/2017 anexo II §5.b',
        message: 'No constan las operaciones realizadas',
      });
    }
    if (isBlank(detail.result)) {
      issues.push({
        field: 'result',
        equipmentId: detail.equipmentId,
        articleRef: 'RD 513/2017 anexo II §5.b',
        message: 'No consta el resultado de las operaciones',
      });
    }
    // «En caso de incidencias, acciones propuestas»: la condición es la
    // incidencia, no el criterio del técnico.
    if (detail.hasIncident && detail.proposedActions.length === 0) {
      issues.push({
        field: 'proposedActions',
        equipmentId: detail.equipmentId,
        articleRef: 'RD 513/2017 anexo II §5.b',
        message: 'Hay incidencia y el acta no propone ninguna acción',
      });
    }
  }

  // ADR-007: sin versión normativa congelada, el acta no se puede
  // reproducir dentro de cinco años (art. 21.2).
  if (isBlank(content.regulationVersionId)) {
    issues.push({
      field: 'regulationVersionId',
      articleRef: 'ADR-007',
      message: 'El acta no fija la versión normativa con la que se ejecutó',
    });
  }

  return { conforme: issues.length === 0, issues };
}

/**
 * ¿Se puede emitir el acta?
 *
 * Se comprueba **antes** de generar el PDF. Generar un documento sellado y
 * descubrir después que le falta un dato obligatorio obliga a sustituirlo,
 * y el histórico se queda con los dos.
 */
export function assertActaEmittable(content: ActaContent): void {
  const validation = validateActa(content);
  if (validation.conforme) return;
  throw new ValidationError(
    `El acta no cumple el contenido mínimo del anexo II §5 (${validation.issues.length} incumplimientos)`,
    { issues: validation.issues },
  );
}

/**
 * ¿Está lista para archivarse?
 *
 * D-06 exige las **dos** firmas. Con una sola, el acta existe pero no es la
 * que el art. 21.2 obliga a tener a disposición de la CCAA.
 */
export function actaSignatureStatus(content: ActaContent): {
  readonly complete: boolean;
  readonly missing: readonly string[];
} {
  const missing: string[] = [];
  if (!content.maintainerSignatureId) missing.push('firma de la empresa mantenedora');
  if (!content.ownerSignatureId) missing.push('firma del representante de la propiedad');
  return { complete: missing.length === 0, missing };
}

// ═══════════════════════════════════════════════════════════════════════
// 6. Informe técnico del art. 17.c — documento DISTINTO del acta
// ═══════════════════════════════════════════════════════════════════════

/**
 * Motivos por los que un equipo entra en el informe del art. 17.c.
 *
 * Literal del artículo: equipos «sin garantía de funcionamiento», «con
 * deficiencias no corregibles», «no conformes» o «no adecuados al riesgo».
 */
export const TECHNICAL_REPORT_REASONS = [
  'SIN_GARANTIA_FUNCIONAMIENTO',
  'DEFICIENCIA_NO_CORREGIBLE',
  'NO_CONFORME',
  'NO_ADECUADO_AL_RIESGO',
] as const;
export type TechnicalReportReason = (typeof TECHNICAL_REPORT_REASONS)[number];

export interface TechnicalReportCandidate {
  readonly equipmentId: string;
  readonly reason: TechnicalReportReason;
  readonly detail: string;
}

export interface TechnicalReportInput {
  readonly equipmentId: string;
  /** El resultado del acta. */
  readonly resultCode: string | null;
  readonly outOfService: boolean;
  /** Fin de vida útil o retimbrados agotados: no hay reparación posible. */
  readonly endOfLifeReached: boolean;
  /** El equipo no corresponde al riesgo del emplazamiento. */
  readonly unsuitableForRisk: boolean;
  /** Anomalías abiertas marcadas como no corregibles. */
  readonly uncorrectableAnomalies: number;
}

/**
 * Selecciona los equipos que deben figurar en el informe al titular.
 *
 * ⚠️ Es un documento **distinto del acta** (fila D-01). Meterlo dentro del
 * acta como un apartado más parece más cómodo, pero el art. 17.c obliga a
 * *entregarlo* al titular: si va escondido en la página 14 de un acta de
 * 60 páginas, la obligación de informar no se ha cumplido de verdad, y es
 * el titular quien responde de un equipo que no protege.
 */
export function selectTechnicalReportItems(
  inputs: readonly TechnicalReportInput[],
  classifications: readonly ResultClassification[],
): TechnicalReportCandidate[] {
  const byCode = new Map(classifications.map((c) => [c.code, c]));
  const candidates: TechnicalReportCandidate[] = [];

  for (const input of inputs) {
    const classification = input.resultCode ? byCode.get(input.resultCode) : undefined;

    if (input.outOfService || classification?.blocksClosure) {
      candidates.push({
        equipmentId: input.equipmentId,
        reason: 'SIN_GARANTIA_FUNCIONAMIENTO',
        detail: 'El equipo no garantiza su funcionamiento en el momento de la revisión',
      });
      continue;
    }
    if (input.endOfLifeReached) {
      candidates.push({
        equipmentId: input.equipmentId,
        reason: 'DEFICIENCIA_NO_CORREGIBLE',
        detail: 'Vida útil agotada: la deficiencia no admite corrección, procede sustitución',
      });
      continue;
    }
    if (input.uncorrectableAnomalies > 0) {
      candidates.push({
        equipmentId: input.equipmentId,
        reason: 'DEFICIENCIA_NO_CORREGIBLE',
        detail: `${input.uncorrectableAnomalies} anomalía(s) declarada(s) no corregible(s)`,
      });
      continue;
    }
    if (input.unsuitableForRisk) {
      candidates.push({
        equipmentId: input.equipmentId,
        reason: 'NO_ADECUADO_AL_RIESGO',
        detail: 'El equipo no es adecuado al riesgo del emplazamiento',
      });
      continue;
    }
    if (classification && classification.severity >= 2) {
      candidates.push({
        equipmentId: input.equipmentId,
        reason: 'NO_CONFORME',
        detail: `Resultado «${classification.label}» en la revisión`,
      });
    }
  }

  return candidates;
}

// ═══════════════════════════════════════════════════════════════════════
// 7. Fotografías contextuales (§35)
// ═══════════════════════════════════════════════════════════════════════

/**
 * Contexto de una fotografía.
 *
 * El §35 es explícito: «no usar fotografías únicamente como archivos
 * adjuntos». Una foto colgada de la OT sirve para mirarla ese día; la misma
 * foto colgada del equipo sirve para comparar su estado seis años después,
 * que es lo que se necesita cuando alguien discute si un defecto ya estaba.
 */
export interface PhotoContext {
  readonly customerId?: string | null;
  readonly siteId?: string | null;
  readonly equipmentId?: string | null;
  readonly workOrderId?: string | null;
  readonly inspectionId?: string | null;
  /** La línea de un equipo dentro de la revisión. */
  readonly inspectionEquipmentId?: string | null;
  readonly checkId?: string | null;
  readonly anomalyId?: string | null;
  readonly locationId?: string | null;
}

/**
 * Claves que anclan una fotografía. Se enumeran explícitamente en lugar de
 * recorrer `Object.values`: quien llama suele pasar un objeto más grande
 * (con la clave de almacenamiento, el hash, el tamaño…) y cualquiera de
 * esos campos daría el contexto por bueno sin que la foto cuelgue de nada.
 */
const PHOTO_ANCHORS = [
  'customerId', 'siteId', 'equipmentId', 'workOrderId', 'inspectionId',
  'inspectionEquipmentId', 'checkId', 'anomalyId', 'locationId',
] as const;

export function assertPhotoContext(context: PhotoContext): void {
  const anchored = PHOTO_ANCHORS.some((key) => context[key] != null);
  if (!anchored) {
    throw new ValidationError(
      'Una fotografía sin contexto es un archivo suelto: debe colgar al menos de un equipo, una revisión, una anomalía o una ubicación',
    );
  }
}

function isBlank(value: unknown): boolean {
  return value == null || (typeof value === 'string' && value.trim() === '');
}
